import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Tags } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "none";
const key = (c) => c || NONE;
const Num = ({ n, onClick, testId }) => (
  <td className="px-3 py-2 text-right font-mono">
    {n ? <button type="button" className="font-semibold underline-offset-2 hover:underline" onClick={onClick} data-testid={testId}>{n}</button> : <span className="text-muted-foreground">—</span>}
  </td>
);

const AssignDialog = ({ w, rows, onClose, onDone }) => {
  const { parties } = useData();
  const [cust, setCust] = useState("");
  const [item, setItem] = useState("all");
  const free = rows.filter((r) => !r.customer_id);
  const items = [...new Map(free.map((r) => [r.item_id, r])).values()];
  const count = free.filter((r) => item === "all" || r.item_id === item).reduce((s, r) => s + r.qty, 0);
  const save = async () => {
    try {
      const r = await api.post("/warehouse/serials/assign-warehouse", { warehouse_id: w.id, customer_id: cust, item_id: item === "all" ? null : item });
      toast.success(`Committente assegnata a ${r.data.updated} apparati`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="devstock-assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Assegna committente</DialogTitle></DialogHeader>
        <p className="text-sm">{w.name}: la committente viene assegnata solo agli apparati a magazzino ancora <b>senza committente</b>.</p>
        <Select value={cust || undefined} onValueChange={setCust}>
          <SelectTrigger data-testid="devstock-assign-customer"><SelectValue placeholder="Seleziona committente" /></SelectTrigger>
          <SelectContent>{parties.filter((p) => p.roles.includes("customer")).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={item} onValueChange={setItem}>
          <SelectTrigger data-testid="devstock-assign-item"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli apparati</SelectItem>{items.map((i) => <SelectItem key={i.item_id} value={i.item_id}>{i.code} · {i.name}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-sm" data-testid="devstock-assign-count">Verranno aggiornati <b>{count}</b> seriali.</p>
        <DialogFooter><Button disabled={!cust || !count} onClick={save} data-testid="devstock-assign-save">Assegna a {count} seriali</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WhRows = ({ w, rows, cols, open, toggle, onOpen, onAssign }) => {
  const sum = (filter) => rows.filter(filter).reduce((s, r) => s + r.qty, 0);
  const items = [...new Map(rows.map((r) => [r.item_id, r])).values()].sort((a, b) => a.code.localeCompare(b.code));
  return (
    <>
      <tr className="border-b bg-white" data-testid={`devstock-row-${w.id}`}>
        <td className="px-3 py-2"><button type="button" className="flex items-center gap-1 text-left text-sm font-semibold" onClick={toggle} data-testid={`devstock-toggle-${w.id}`}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{w.kind === "VAN" ? "Furgone · " : ""}{w.name.replace(/^Furgone /, "")}</button>
          {onAssign && rows.some((r) => !r.customer_id) && <Button size="sm" variant="outline" className="ml-6 mt-1 h-7 text-xs" onClick={onAssign} data-testid={`devstock-assign-${w.id}`}><Tags className="mr-1 h-3.5 w-3.5" />Assegna committente</Button>}</td>
        {cols.map((c) => <Num key={c.id} n={sum((r) => key(r.customer_id) === c.id)} onClick={() => onOpen({ warehouse_id: w.id, customer_id: c.id })} testId={`devstock-${w.id}-${c.id}`} />)}
        <td className="px-3 py-2 text-right font-mono font-bold" data-testid={`devstock-total-${w.id}`}>{sum(() => true)}</td>
      </tr>
      {open && items.map((it) => (
        <tr key={it.item_id} className="border-b bg-muted/30 text-sm" data-testid={`devstock-item-${w.id}-${it.code}`}>
          <td className="py-1.5 pl-9 pr-3"><span className="font-mono text-xs">{it.code}</span> · {it.name}</td>
          {cols.map((c) => <Num key={c.id} n={sum((r) => r.item_id === it.item_id && key(r.customer_id) === c.id)} onClick={() => onOpen({ warehouse_id: w.id, customer_id: c.id, item_id: it.item_id })} testId={`devstock-${w.id}-${c.id}-${it.code}`} />)}
          <td className="px-3 py-1.5 text-right font-mono">{sum((r) => r.item_id === it.item_id)}</td>
        </tr>
      ))}
    </>
  );
};

export const DevicesByCustomer = ({ warehouses, wh, refreshKey, onOpenSerials }) => {
  const { nameOf } = useData();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState({});
  const [assign, setAssign] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/warehouse/stock/devices").then((r) => setRows(r.data)).catch(() => {}); }, [refreshKey, tick]);
  const whs = warehouses.filter((w) => (!wh || w.id === wh) && rows.some((r) => r.warehouse_id === w.id));
  const shown = rows.filter((r) => whs.some((w) => w.id === r.warehouse_id));
  const custIds = [...new Set(shown.map((r) => r.customer_id).filter(Boolean))].sort((a, b) => nameOf("parties", a).localeCompare(nameOf("parties", b)));
  const cols = [...custIds.map((id) => ({ id, label: nameOf("parties", id) })), ...(shown.some((r) => !r.customer_id) ? [{ id: NONE, label: "Senza committente" }] : [])];
  if (!whs.length) return <Empty text="Nessun apparato a magazzino" testId="devstock-empty" />;
  const tot = (c) => shown.filter((r) => key(r.customer_id) === c.id).reduce((s, r) => s + r.qty, 0);
  return (
    <div className="overflow-x-auto border bg-white" data-testid="devstock-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-muted/50 text-left text-xs font-semibold uppercase text-muted-foreground">
          <th className="px-3 py-2">Magazzino / furgone</th>{cols.map((c) => <th key={c.id} className="px-3 py-2 text-right" data-testid={`devstock-col-${c.id}`}>{c.label}</th>)}<th className="px-3 py-2 text-right">Totale</th></tr></thead>
        <tbody>
          {whs.map((w) => <Fragment key={w.id}><WhRows w={w} rows={shown.filter((r) => r.warehouse_id === w.id)} cols={cols} open={!!open[w.id]} toggle={() => setOpen({ ...open, [w.id]: !open[w.id] })} onOpen={onOpenSerials} onAssign={can("inventory:write") ? () => setAssign(w) : null} /></Fragment>)}
          <tr className="font-semibold" data-testid="devstock-grand-total"><td className="px-3 py-2">Totale</td>{cols.map((c) => <td key={c.id} className="px-3 py-2 text-right font-mono">{tot(c)}</td>)}<td className="px-3 py-2 text-right font-mono">{shown.reduce((s, r) => s + r.qty, 0)}</td></tr>
        </tbody>
      </table>
      {assign && <AssignDialog w={assign} rows={shown.filter((r) => r.warehouse_id === assign.id)} onClose={() => setAssign(null)} onDone={() => { setAssign(null); setTick((t) => t + 1); }} />}
      <p className="border-t px-3 py-2 text-xs text-muted-foreground">Apparati a seriale disponibili (a magazzino). Clicca una riga per il dettaglio per apparato, clicca un numero per vedere i seriali.</p>
    </div>
  );
};
